import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { usePingEngineMutation, useGetUserConnectedClientsMutation } from '@common-reducers/backend-api/backend-api';
import { selectConnectedSockets, ConnectedSocket } from '@common-reducers/ConnectedEnginesSlice';
import { CommonRootState } from '@common/types/common-root-state-type';
import { NotifierConnectionStatus } from '@common/noifier-module/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { RefreshCw, Wifi, WifiOff, Activity, Clock, Server, Globe, Monitor, Smartphone, Database, AlertTriangle } from 'lucide-react';

interface EngineHealth {
  status: 'alive' | 'dead' | 'pinging' | 'unknown';
  lastPingTime?: number;
  pingLatency?: number;
  errorMessage?: string;
  consecutiveFailures?: number;
}

function formatTimestamp(ts?: number): string {
  if (!ts) return 'N/A';
  const date = new Date(ts * 1000);
  return date.toLocaleString();
}

function formatTimeAgo(ts?: number): string {
  if (!ts) return 'N/A';
  const now = Math.floor(Date.now() / 1000);
  const diff = now - ts;

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatConnectionTime(connectionTime?: string): string {
  if (!connectionTime) return 'N/A';
  try {
    const date = new Date(connectionTime);
    return date.toLocaleString();
  } catch {
    return connectionTime;
  }
}

function getHealthColor(health: EngineHealth['status']): string {
  switch (health) {
    case 'alive': return 'bg-green-500';
    case 'dead': return 'bg-red-500';
    case 'pinging': return 'bg-yellow-500 animate-pulse';
    default: return 'bg-gray-400';
  }
}

function getHealthBadge(health: EngineHealth['status']): { variant: 'default' | 'destructive' | 'outline' | 'secondary'; text: string } {
  switch (health) {
    case 'alive': return { variant: 'default', text: 'Online' };
    case 'dead': return { variant: 'destructive', text: 'Dead' };
    case 'pinging': return { variant: 'secondary', text: 'Pinging...' };
    default: return { variant: 'outline', text: 'Unknown' };
  }
}

function getBrowserTypeIcon(browserType?: string) {
  switch (browserType) {
    case 'WhatsApp Web': return <Monitor className="h-4 w-4" />;
    case 'Gateway': return <Server className="h-4 w-4" />;
    case 'Website': return <Globe className="h-4 w-4" />;
    default: return <Smartphone className="h-4 w-4" />;
  }
}

function isSocketStale(ts?: number): boolean {
  if (!ts) return false;
  const now = Math.floor(Date.now() / 1000);
  const STALE_THRESHOLD = 300; // 5 minutes
  return (now - ts) > STALE_THRESHOLD;
}

export default function OnlineEnginesView() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('supportedUserId') ?? '';

  const localSockets = useSelector(selectConnectedSockets);
  const connectionStatus = useSelector((state: CommonRootState) => state.NotifierReducer?.connectionStatus);
  const [pingEngine] = usePingEngineMutation();
  const [getUserConnectedClients, { isLoading: isLoadingClients }] = useGetUserConnectedClientsMutation();

  const [engineHealth, setEngineHealth] = useState<Record<string, EngineHealth>>({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [selectedSocket, setSelectedSocket] = useState<string | null>(null);
  const [apiSockets, setApiSockets] = useState<ConnectedSocket[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'api' | 'mqtt' | 'both'>('both');

  const isNotifierConnected = connectionStatus === NotifierConnectionStatus.AUTHENTICATED;

  // Fetch engines from backend API for supported user
  const fetchEnginesFromApi = useCallback(async () => {
    if (!userId) return;

    setApiError(null);
    try {
      const result = await getUserConnectedClients({ userId }).unwrap();
      console.log('[OnlineEngines] API returned engines:', result);
      setApiSockets(result || []);
      setLastRefresh(Date.now());
    } catch (error: any) {
      console.error('[OnlineEngines] API error:', error);
      setApiError(error?.data?.message || error?.message || 'Failed to fetch engines');
      setApiSockets([]);
    }
  }, [userId, getUserConnectedClients]);

  // Fetch on mount and when userId changes
  useEffect(() => {
    if (userId) {
      fetchEnginesFromApi();
    }
  }, [userId, fetchEnginesFromApi]);

  // Combine API sockets with local MQTT sockets, deduplicating by ID
  const allSockets = useMemo(() => {
    const socketMap = new Map<string, ConnectedSocket>();

    // Add local sockets first (if no userId filter, or matches userId)
    for (const socket of localSockets) {
      if (!socket || !socket.id) continue;
      if (userId && !socket.id.includes(userId)) continue;
      socketMap.set(socket.id, socket);
    }

    // Merge API sockets (prefer API data as it's from EMQX directly)
    for (const socket of apiSockets) {
      if (!socket || !socket.id) continue;
      // API sockets might have more accurate data, so override
      socketMap.set(socket.id, socket);
    }

    return Array.from(socketMap.values());
  }, [localSockets, apiSockets, userId]);

  // Filter sockets: valid sockets with required fields
  const filteredSockets = useMemo(() => {
    return allSockets.filter(socket => {
      if (!socket) return false;
      // Only require id, be lenient on other fields since API might not have all
      if (!socket.id) return false;
      // Filter by browserType if available
      if (socket.browserType && !['WhatsApp Web', 'Gateway', 'Website'].includes(socket.browserType)) {
        return false;
      }
      return true;
    });
  }, [allSockets]);

  // Separate by type first: WhatsApp Web vs Website
  const { whatsappSockets, websiteSockets } = useMemo(() => {
    const whatsapp: ConnectedSocket[] = [];
    const website: ConnectedSocket[] = [];

    for (const socket of filteredSockets) {
      if (socket.browserType === 'WhatsApp Web') {
        whatsapp.push(socket);
      } else {
        website.push(socket);
      }
    }

    return { whatsappSockets: whatsapp, websiteSockets: website };
  }, [filteredSockets]);

  // Separate by health status
  const { onlineSockets, staleSockets, zombieSockets, deadSockets } = useMemo(() => {
    const online: ConnectedSocket[] = [];
    const stale: ConnectedSocket[] = [];
    const zombie: ConnectedSocket[] = [];
    const dead: ConnectedSocket[] = [];

    for (const socket of filteredSockets) {
      const health = engineHealth[socket.id]?.status;
      const isStale = isSocketStale(socket.ts);

      if (health === 'dead') {
        // Zombie: ping failed but heartbeat is recent
        if (!isStale && socket.ts) {
          zombie.push(socket);
        } else {
          dead.push(socket);
        }
      } else if (isStale) {
        stale.push(socket);
      } else {
        online.push(socket);
      }
    }

    return { onlineSockets: online, staleSockets: stale, zombieSockets: zombie, deadSockets: dead };
  }, [filteredSockets, engineHealth]);

  // Separate WhatsApp sockets by health
  const { whatsappOnline, whatsappZombie, whatsappStale, whatsappDead } = useMemo(() => {
    const online: ConnectedSocket[] = [];
    const zombie: ConnectedSocket[] = [];
    const stale: ConnectedSocket[] = [];
    const dead: ConnectedSocket[] = [];

    for (const socket of whatsappSockets) {
      const health = engineHealth[socket.id]?.status;
      const isStale = isSocketStale(socket.ts);

      if (health === 'dead') {
        if (!isStale && socket.ts) {
          zombie.push(socket);
        } else {
          dead.push(socket);
        }
      } else if (isStale) {
        stale.push(socket);
      } else {
        online.push(socket);
      }
    }

    return { whatsappOnline: online, whatsappZombie: zombie, whatsappStale: stale, whatsappDead: dead };
  }, [whatsappSockets, engineHealth]);

  // State for collapsing website section
  const [showWebsiteSockets, setShowWebsiteSockets] = useState(false);

  // Ping a single engine
  const pingSingleEngine = useCallback(async (engineId: string) => {
    setEngineHealth(prev => ({
      ...prev,
      [engineId]: { ...prev[engineId], status: 'pinging' }
    }));

    const startTime = Date.now();
    try {
      const result = await pingEngine({ engineId }).unwrap();
      const latency = Date.now() - startTime;
      setEngineHealth(prev => ({
        ...prev,
        [engineId]: {
          status: result?.success ? 'alive' : 'dead',
          lastPingTime: Date.now(),
          pingLatency: latency,
          errorMessage: result?.success ? undefined : 'Ping returned failure',
          consecutiveFailures: result?.success ? 0 : (prev[engineId]?.consecutiveFailures || 0) + 1
        }
      }));
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.message || 'Request timed out';
      console.error(`[OnlineEngines] Ping failed for ${engineId}:`, errorMsg);
      setEngineHealth(prev => ({
        ...prev,
        [engineId]: {
          status: 'dead',
          lastPingTime: Date.now(),
          pingLatency: undefined,
          errorMessage: errorMsg,
          consecutiveFailures: (prev[engineId]?.consecutiveFailures || 0) + 1
        }
      }));
    }
  }, [pingEngine]);

  // Update health for all engines:
  // - WhatsApp engines: ping them to verify they respond
  // - Website connections: determine status from heartbeat timestamp (can't be pinged)
  const pingAllEngines = useCallback(async () => {
    setLastRefresh(Date.now());

    // For Website sockets, determine health from heartbeat (no ping needed)
    // If heartbeat is recent (not stale), mark as alive; otherwise mark as dead
    for (const socket of websiteSockets) {
      if (!socket.id) continue;
      const isStale = isSocketStale(socket.ts);
      setEngineHealth(prev => ({
        ...prev,
        [socket.id]: {
          status: isStale ? 'dead' : 'alive',
          lastPingTime: Date.now(),
          pingLatency: 0,
          errorMessage: isStale ? 'No heartbeat received (5+ min)' : undefined,
          consecutiveFailures: 0
        }
      }));
    }

    // For WhatsApp engines, actually ping them
    const pingPromises = whatsappSockets
      .filter(socket => socket.id)
      .map(socket => pingSingleEngine(socket.id));
    await Promise.allSettled(pingPromises);
  }, [whatsappSockets, websiteSockets, pingSingleEngine]);

  // Initial health check when sockets change
  const prevSocketIdsRef = useRef<string>('');
  useEffect(() => {
    const socketIds = filteredSockets.map(s => s.id).sort().join(',');
    if (socketIds && socketIds !== prevSocketIdsRef.current) {
      prevSocketIdsRef.current = socketIds;
      pingAllEngines();
    }
  }, [filteredSockets, pingAllEngines]);

  // Auto-refresh API data and update engine health
  useEffect(() => {
    if (!autoRefresh) return;

    const timer = setInterval(() => {
      if (userId) fetchEnginesFromApi();
      // Re-check all engines (pings WhatsApp, updates Website from heartbeat)
      if (filteredSockets.length > 0) {
        console.log('[OnlineEngines] Auto-refresh: updating engine health');
        pingAllEngines();
      }
    }, 60000); // Refresh every 60 seconds (reduced from 30s to prevent request pile-up)

    return () => clearInterval(timer);
  }, [autoRefresh, userId, fetchEnginesFromApi, filteredSockets.length, pingAllEngines]);

  // Force re-render for time-ago updates. Display-only ("Xm ago"
  // labels), so 30s is plenty — a 1s full-view re-render was pure
  // wasted CPU — and skip entirely while the tab is hidden.
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      if (!document.hidden) forceUpdate(n => n + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const renderSocketCard = (socket: ConnectedSocket, isStale: boolean = false) => {
    const health = engineHealth[socket.id] || { status: 'unknown' };
    const healthBadge = getHealthBadge(health.status);
    const isSelected = selectedSocket === socket.id;

    return (
      <div
        key={socket.id}
        className={cn(
          "border rounded-lg p-4 transition-all cursor-pointer",
          isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700",
          isStale && "border-orange-300 bg-orange-50 dark:bg-orange-900/10"
        )}
        onClick={() => setSelectedSocket(isSelected ? null : socket.id)}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={cn("w-3 h-3 rounded-full", getHealthColor(health.status))} />
            {getBrowserTypeIcon(socket.browserType)}
            <span className="font-medium text-sm">{socket.browserType || 'Unknown'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={healthBadge.variant}>{healthBadge.text}</Badge>
            {health.pingLatency && (
              <Badge variant="outline" className="text-xs">
                {health.pingLatency}ms
              </Badge>
            )}
          </div>
        </div>

        {/* Socket ID */}
        <div className="text-xs font-mono text-gray-600 dark:text-gray-400 mb-2 break-all">
          {socket.id}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1 text-gray-500">
            <Clock className="h-3 w-3" />
            <span>Last seen: {formatTimeAgo(socket.ts)}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-500">
            <Activity className="h-3 w-3" />
            <span>Domain: {socket.domain || 'N/A'}</span>
          </div>
        </div>

        {/* Zombie Socket Warning - heartbeat recent but ping failed */}
        {health.status === 'dead' && socket.ts && !isSocketStale(socket.ts) && (
          <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-xs">
            <div className="flex items-center gap-1 text-red-700 dark:text-red-300 font-medium">
              <AlertTriangle className="h-3 w-3" />
              <span>ZOMBIE SOCKET</span>
            </div>
            <p className="text-red-600 dark:text-red-400 mt-1">
              Heartbeat OK ({formatTimeAgo(socket.ts)}) but ping failed. Socket is connected but not responding to commands.
            </p>
            {health.errorMessage && (
              <p className="text-red-500 dark:text-red-400 mt-1 font-mono">
                Error: {health.errorMessage}
              </p>
            )}
            {health.consecutiveFailures && health.consecutiveFailures > 1 && (
              <p className="text-red-500 dark:text-red-400 mt-1">
                Failed {health.consecutiveFailures} consecutive times
              </p>
            )}
          </div>
        )}

        {/* Dead Socket with error */}
        {health.status === 'dead' && isSocketStale(socket.ts) && health.errorMessage && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <WifiOff className="h-3 w-3" />
            <span>Error: {health.errorMessage}</span>
          </div>
        )}

        {/* Stale Warning */}
        {isStale && health.status !== 'dead' && (
          <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
            <WifiOff className="h-3 w-3" />
            <span>Socket may be stale (no heartbeat for 5+ minutes)</span>
          </div>
        )}

        {/* Expanded Details */}
        {isSelected && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Client ID:</span>
                <p className="font-mono text-xs break-all">{socket.clientId || socket.id}</p>
              </div>
              <div>
                <span className="text-gray-500">Device:</span>
                <p>{socket.device || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Connected From:</span>
                <p>{socket.connectedFrom || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Connection Time:</span>
                <p>{formatConnectionTime(socket.connectionTime)}</p>
              </div>
              <div>
                <span className="text-gray-500">Last Heartbeat (ts):</span>
                <p>{socket.ts ? formatTimestamp(socket.ts) : 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">App Version:</span>
                <p>{socket.app || 'N/A'}</p>
              </div>
            </div>

            {/* Ping Status Section */}
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium mb-2">Ping Status</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Last Ping:</span>
                  <p>{health.lastPingTime ? new Date(health.lastPingTime).toLocaleTimeString() : 'Never'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Latency:</span>
                  <p>{health.pingLatency ? `${health.pingLatency}ms` : 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className={cn(
                    health.status === 'alive' && 'text-green-600',
                    health.status === 'dead' && 'text-red-600',
                    health.status === 'pinging' && 'text-yellow-600'
                  )}>
                    {health.status.toUpperCase()}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Consecutive Failures:</span>
                  <p className={cn(
                    health.consecutiveFailures && health.consecutiveFailures > 0 && 'text-red-600'
                  )}>
                    {health.consecutiveFailures || 0}
                  </p>
                </div>
              </div>
              {health.errorMessage && (
                <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                  <span className="text-red-600 dark:text-red-400 font-mono">{health.errorMessage}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  pingSingleEngine(socket.id);
                }}
                disabled={health.status === 'pinging'}
              >
                <RefreshCw className={cn("h-3 w-3 mr-1", health.status === 'pinging' && "animate-spin")} />
                Ping
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-[1600px] mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Monitor className="h-6 w-6 text-green-600" />
            WhatsApp Web Monitor
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Real-time status of WhatsApp Web engines for messaging
            {userId && <span className="ml-2 font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">User: {userId}</span>}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* MQTT Connection Status */}
          <Badge variant={isNotifierConnected ? "default" : "destructive"} className="h-8">
            {isNotifierConnected ? (
              <><Wifi className="h-3 w-3 mr-1" /> MQTT</>
            ) : (
              <><WifiOff className="h-3 w-3 mr-1" /> MQTT</>
            )}
          </Badge>

          {/* API Status */}
          <Badge variant={apiSockets.length > 0 ? "default" : "outline"} className="h-8">
            <Database className="h-3 w-3 mr-1" />
            API: {apiSockets.length}
          </Badge>

          {/* Auto Refresh Toggle */}
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <Activity className={cn("h-4 w-4 mr-1", autoRefresh && "animate-pulse")} />
            Auto
          </Button>

          {/* Refresh API */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchEnginesFromApi}
            disabled={isLoadingClients}
          >
            <RefreshCw className={cn("h-4 w-4 mr-1", isLoadingClients && "animate-spin")} />
            Refresh
          </Button>

          {/* Ping All */}
          <Button
            variant="outline"
            size="sm"
            onClick={pingAllEngines}
            disabled={filteredSockets.length === 0}
          >
            <Activity className="h-4 w-4 mr-1" />
            Ping All
          </Button>
        </div>
      </div>

      {/* API Error Alert */}
      {apiError && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-800 dark:text-red-200">Error fetching engines from API</h4>
            <p className="text-sm text-red-600 dark:text-red-300 mt-1">{apiError}</p>
            <p className="text-xs text-red-500 dark:text-red-400 mt-2">
              Showing data from local MQTT presence only. Backend API may be unavailable.
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoadingClients && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
          <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
          <span className="text-blue-700 dark:text-blue-300">Fetching engines from EMQX API...</span>
        </div>
      )}

      {/* WhatsApp Web Stats - Primary Focus */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Monitor className="h-5 w-5 text-green-600" />
          WhatsApp Web Engines
        </h2>
        <div className="grid grid-cols-4 gap-4">
          <div className={cn(
            "border-2 rounded-lg p-4",
            whatsappOnline.length > 0 ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-gray-200 bg-white dark:bg-gray-800"
          )}>
            <div className="text-4xl font-bold text-green-600">{whatsappOnline.length}</div>
            <div className="text-sm text-green-700 dark:text-green-400 font-medium">Online & Ready</div>
          </div>
          <div className={cn(
            "border-2 rounded-lg p-4",
            whatsappZombie.length > 0 ? "border-red-500 bg-red-50 dark:bg-red-900/20" : "border-gray-200 bg-white dark:bg-gray-800"
          )}>
            <div className="text-4xl font-bold text-red-600">{whatsappZombie.length}</div>
            <div className="text-sm text-red-600 font-medium">Zombie (Not Responding)</div>
          </div>
          <div className={cn(
            "border rounded-lg p-4",
            whatsappStale.length > 0 ? "border-orange-300 bg-orange-50 dark:bg-orange-900/10" : "bg-white dark:bg-gray-800"
          )}>
            <div className="text-3xl font-bold text-orange-500">{whatsappStale.length}</div>
            <div className="text-sm text-gray-500">Stale (5+ min)</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg p-4">
            <div className="text-3xl font-bold">{whatsappSockets.length}</div>
            <div className="text-sm text-gray-500">Total WhatsApp</div>
          </div>
        </div>
      </div>

      {/* Website Summary - Collapsed */}
      <div className="mb-6">
        <button
          onClick={() => setShowWebsiteSockets(!showWebsiteSockets)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <Globe className="h-4 w-4" />
          <span>Website Connections: {websiteSockets.length}</span>
          <span className="text-xs">({showWebsiteSockets ? 'click to hide' : 'click to show'})</span>
        </button>
      </div>

      {/* ==================== WhatsApp Web Section ==================== */}

      {/* WhatsApp Zombie Sockets - CRITICAL - Show First */}
      {whatsappZombie.length > 0 && (
        <div className="mb-6">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg mb-4">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
              <Monitor className="h-5 w-5" />
              WhatsApp Zombie Engines ({whatsappZombie.length}) - CRITICAL
            </h2>
            <p className="text-sm text-red-600 dark:text-red-400">
              WhatsApp engines appear connected but are NOT responding to commands.
              Messages will NOT be delivered. Refresh the WhatsApp Web tab or restart the extension.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whatsappZombie.map(socket => renderSocketCard(socket, false))}
          </div>
        </div>
      )}

      {/* WhatsApp Online Engines */}
      {whatsappOnline.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-green-700 dark:text-green-400">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <Monitor className="h-5 w-5" />
            WhatsApp Web - Online ({whatsappOnline.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whatsappOnline.map(socket => renderSocketCard(socket, false))}
          </div>
        </div>
      )}

      {/* WhatsApp Stale Engines */}
      {whatsappStale.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-orange-600">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <Monitor className="h-5 w-5" />
            WhatsApp Web - Stale ({whatsappStale.length})
            <span className="text-sm font-normal text-gray-500">- No heartbeat for 5+ minutes</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whatsappStale.map(socket => renderSocketCard(socket, true))}
          </div>
        </div>
      )}

      {/* WhatsApp Dead Engines */}
      {whatsappDead.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-gray-600">
            <div className="w-3 h-3 rounded-full bg-gray-500" />
            <Monitor className="h-5 w-5" />
            WhatsApp Web - Dead ({whatsappDead.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whatsappDead.map(socket => renderSocketCard(socket, false))}
          </div>
        </div>
      )}

      {/* No WhatsApp Engines */}
      {whatsappSockets.length === 0 && (
        <div className="mb-8 p-6 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 rounded-lg text-center">
          <Monitor className="h-12 w-12 mx-auto text-yellow-500 mb-3" />
          <h3 className="text-lg font-semibold text-yellow-700 dark:text-yellow-300">No WhatsApp Web Connected</h3>
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
            Open WhatsApp Web with the Blueticks extension to enable messaging features.
          </p>
        </div>
      )}

      {/* ==================== Website Section (Collapsible) ==================== */}
      {showWebsiteSockets && websiteSockets.length > 0 && (
        <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <h2 className="text-md font-semibold mb-3 flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Globe className="h-4 w-4" />
            Website Connections ({websiteSockets.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {websiteSockets.map(socket => renderSocketCard(socket, isSocketStale(socket.ts)))}
          </div>
        </div>
      )}

      {/* Empty State - Only show if no sockets at all AND no API user specified */}
      {filteredSockets.length === 0 && !userId && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <WifiOff className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300">No Engines Found</h3>
          <p className="text-sm text-gray-500 mt-1">
            {userId
              ? `No connected engines found for user ${userId}`
              : 'No connected engines found. Open WhatsApp Web with the extension installed.'
            }
          </p>
        </div>
      )}

      {/* Debug Info */}
      <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="font-semibold text-sm mb-2">Connection Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-xs text-gray-500">
          <div>
            <span className="font-medium text-green-600">WhatsApp Online:</span>
            <p className="text-lg font-bold text-green-600">{whatsappOnline.length}</p>
          </div>
          <div>
            <span className="font-medium text-red-600">WhatsApp Zombie:</span>
            <p className="text-lg font-bold text-red-600">{whatsappZombie.length}</p>
          </div>
          <div>
            <span className="font-medium">Website:</span>
            <p className="text-lg font-bold">{websiteSockets.length}</p>
          </div>
          <div>
            <span className="font-medium">Last Refresh:</span>
            <p>{new Date(lastRefresh).toLocaleTimeString()}</p>
          </div>
          <div>
            <span className="font-medium">Auto Refresh:</span>
            <p>{autoRefresh ? 'Enabled (60s)' : 'Disabled'}</p>
          </div>
          <div>
            <span className="font-medium">MQTT Status:</span>
            <p>{connectionStatus || 'Unknown'}</p>
          </div>
          <div>
            <span className="font-medium">Total Engines:</span>
            <p>{filteredSockets.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
